// src/Containers/Repertoire/RepertoirePlay.tsx

import { Box, Button, MenuItem, TextField, useMediaQuery } from '@mui/material';
import { useAudioRecognition } from 'AudioRecognition';
import type { RecognitionFrame } from 'AudioRecognition/types';
import RepertoirePlayContainerOSMD from './RepertoirePlayContainerOSMD';
import { useFeatureFlag, FeatureFlag } from 'Utils/FeatureFlags';
import RepBackArrow from 'assets/images/Repertoire/RepBackArrow.png';
import AccuracyMusicIcon from 'assets/images/Repertoire/AccuracyMusicIcon.png';
import PerformanceIcon from 'assets/images/Repertoire/PerformanceIcon.png';
import HandActive from 'assets/images/Repertoire/HandActive.png';
import HandInactive from 'assets/images/Repertoire/HandInactive.png';
import Restart from 'assets/images/Repertoire/Restart.png';
import RestartBig from 'assets/images/Repertoire/RestartBig.png';
import Play from 'assets/images/Play.png';
import SettingsIcon from 'assets/images/Repertoire/SettingsIcon.png';
import { useDispatch, useSelector } from 'react-redux';
import * as appActions from 'Actions/app';
import * as repertoireActions from 'Actions/repertoire';
import { MainAppReducer } from 'Types';
import {
  RepertoireReducer,
  selectPerfectPhraseEnabled,
  selectPerfectPhraseCount,
} from 'Types/RepertoireTypes';
import { useLocation } from 'react-router-dom';
import React, { useState, useEffect, useLayoutEffect, useCallback, useRef, useMemo } from 'react';
import { useRepertoirePlayContext } from 'Contexts/RepertoirePlayContext';
import Fullscreen from 'Components/Fullscreen';
import { find } from 'lodash';
import {
  NotifyPhaserEvents,
  NotifyReactEvents,
  PhaserToReactListeners,
  RemoveListener,
} from 'Phaser/EventBus2';
import { AppTourStepHelper } from 'Phaser/EventBus2/ToReact';
import { CustomSwitch } from 'Components';
import { useMidiContext } from 'Contexts/MidiContext';
import { getDeviceClass } from 'Utils/Responsive';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Add, Remove } from '@mui/icons-material';
import { isCapacitorNative } from 'Utils/Constants';
import { useTheaterUI } from './Utils';
import AppTour, {
  AppTourStep,
  TooltipRelativePosition,
  filterByUnseenFeatures,
} from 'Components/AppTour';
import { ApptourFeature, ApptourFeatureItem } from 'Utils/AppTourFeatures';
import IconButton from '@mui/material/IconButton';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { ReactComponent as LoopGlyph } from 'assets/images/loopGlyph.svg';
import PlusButton from 'assets/images/plusButton.svg';
import MinusButton from 'assets/images/minusButton.svg';
import ReplayIcon from '@mui/icons-material/Replay';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import HeadphonesRoundedIcon from '@mui/icons-material/HeadphonesRounded';
import PianoRoundedIcon from '@mui/icons-material/PianoRounded';
import { MODE_CONVERSION_OPTIONS, type ParallelModeConversion } from 'Utils/MusicXmlModeConversion';
import TempoControls from 'Components/Tempo';
import { useHeaderPopupAnchor } from 'Hooks/useHeaderPopupAnchor';
import RepertoireReadoutLabel from './RepertoireReadoutLabel';
import {
  COMPACT_PAUSE_SIZE_PX,
  footerTransportLayout,
  TRANSPORT_OFFSETS,
} from './footerTransportLayout';
import {
  nextRepertoireHeaderFit,
  repertoireHeaderHasRoomForTwoRows,
  repertoireHeaderOneRowHeightPx,
  repertoireScoreExtraTopPx,
  REPERTOIRE_HEADER_ONE_ROW,
  type RepertoireHeaderFit,
} from './repertoireHeaderFit';
import { TempoProvider } from 'Contexts/TempoContext';
import './Repertoire.css';
import {
  transportPlayButtonChrome,
  transportRestartButtonChrome,
  dimPaperRestartIconChrome,
  dimPaperRestartButtonChrome,
} from './shared/playbackChrome';
import {
  REPERTOIRE_PLAYBACK_MODE_OPTIONS,
  repertoirePlaybackModePlaysNotes,
  type RepertoirePlaybackMode,
} from './shared/repertoirePlayback';
import { resolveGoalTempo, resolveInitialRepertoireTempo } from './shared/repertoireTempo';
import { prepareRepertoireNotePlaybackAudio } from './components/useRepertoireNotePlayback';
import { normalizeScoreTheme } from 'Utils/ScoreTheme';
import type { ScoreTheme } from 'Utils/ScoreTheme';
import AudioDiagnosticsPanel from './Create/components/AudioDiagnosticsPanel';
import VolumeControls from 'Volume/VolumeControls';
import { useRepertoirePlayBack } from './useRepertoirePlayBack';
import { useHardwareBackClosesLayer } from 'Hooks/useHardwareBack';
import { volumeControlsAvailable } from 'Volume/volumeAvailability';
import { useEffectiveGrading } from 'Utils/playAlong/effectiveGrading';
import {
  featuresToMarkSeen,
  reportTooltipSeen,
  ungradedRepertoireTourDue,
  withUngradedRepertoireAccuracy,
} from 'Utils/playAlong/ungradedTour';
import {
  REPERTOIRE_ACCURACY_CONTROL_ID,
  notGradedRepertoireStep,
} from './ungradedRepertoireTourSteps';
import { useLatchedWhileOpen } from 'Hooks/useLatchedWhileOpen';
import {
  repSettingsCaretRight,
  repSettingsPanelPlacement,
  repSettingsSideSx,
} from './repSettingsPanelPlacement';
import PlayInputMenuEntry from 'Components/PlayAlong/PlayInputMenuEntry';
import PlayAlongIdlePause from 'Components/PlayAlong/PlayAlongIdlePause';
import { resumeAfterIdlePause } from 'Utils/playAlong/idleResume';
import {
  AUDIO_DIAGNOSTICS_MAX_FRAMES,
  AUDIO_DIAGNOSTICS_UI_INTERVAL_MS,
  buildAudioDiagnosticsFrame,
  buildAudioDiagnosticsSnapshot,
  type AudioDiagnosticsFrame,
  type AudioDiagnosticsSnapshot,
} from './Create/utils/audioDiagnostics';

const MAX_TRANSPOSE_SEMITONES = 7;
const TRANSPOSE_SEMITONE_OPTIONS = Array.from(
  { length: MAX_TRANSPOSE_SEMITONES * 2 + 1 },
  (_, index) => index - MAX_TRANSPOSE_SEMITONES,
);
const REPERTOIRE_SELECT_MENU_Z_INDEX = 5000;
const REPERTOIRE_SELECT_MENU_INTERACTION_SELECTOR =
  '.MuiMenu-paper, .MuiPopover-root, .MuiMenuItem-root, [role="listbox"], [role="option"], .MuiDialog-root';
const REPERTOIRE_PLAY_AUDIO_DIAGNOSTICS_VISIBLE_STORAGE_KEY =
  'repertoire-play-audio-diagnostics-visible-v1';

const readStoredBoolean = (key: string, fallback = false) => {
  if (typeof window === 'undefined') return fallback;

  try {
    const stored = window.localStorage.getItem(key);
    if (stored === 'true') return true;
    if (stored === 'false') return false;
  } catch {
    return fallback;
  }

  return fallback;
};

const writeStoredBoolean = (key: string, value: boolean) => {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(key, value ? 'true' : 'false');
  } catch {
    // Ignore storage failures.
  }
};

const REPERTOIRE_PLAYBACK_MODE_ACCENTS: Record<
  RepertoirePlaybackMode,
  {
    color: string;
    surface: string;
    border: string;
    shadow: string;
  }
> = {
  practice: {
    color: '#40E16D',
    surface: 'rgba(64, 225, 109, 0.10)',
    border: 'rgba(64, 225, 109, 0.46)',
    shadow: '0 0 0 1px rgba(64, 225, 109, 0.18), 0 10px 30px rgba(64, 225, 109, 0.08)',
  },
  listen: {
    color: '#D4866C',
    surface: 'rgba(212, 134, 108, 0.13)',
    border: 'rgba(212, 134, 108, 0.52)',
    shadow: '0 0 0 1px rgba(212, 134, 108, 0.22), 0 10px 30px rgba(212, 134, 108, 0.10)',
  },
};

const getRepertoirePlaybackModeAccent = (mode: RepertoirePlaybackMode) =>
  REPERTOIRE_PLAYBACK_MODE_ACCENTS[mode];

const getRepertoirePlaybackModeIcon = (mode: RepertoirePlaybackMode) => {
  switch (mode) {
    case 'listen':
      return HeadphonesRoundedIcon;
    case 'practice':
    default:
      return PianoRoundedIcon;
  }
};

const getTransposeLabel = (semitoneSteps: number): string => {
  if (semitoneSteps === 0) {
    return 'Concert (0)';
  }
  const absSemitones = Math.abs(semitoneSteps);
  return `${semitoneSteps > 0 ? '+' : ''}${semitoneSteps} ${
    absSemitones === 1 ? 'semitone' : 'semitones'
  }`;
};

const repertoireSettingsSelectSx = {
  width: '100%',
  '& .MuiOutlinedInput-root': {
    minHeight: '42px',
    color: '#ECECED',
    backgroundColor: '#243056',
    borderRadius: '12px',
    fontFamily: 'Lato',
    fontSize: '14px',
    fontWeight: 600,
  },
  '& .MuiSelect-select': {
    display: 'flex',
    alignItems: 'center',
    padding: '8px 36px 8px 10px',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#525969',
    borderWidth: '2px',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: '#6F87F1',
  },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#6F87F1',
  },
  '& .MuiSvgIcon-root': {
    color: '#ECECED',
  },
} as const;

const repertoireSettingsSelectMenuProps = {
  sx: {
    zIndex: REPERTOIRE_SELECT_MENU_Z_INDEX,
  },
  PaperProps: {
    sx: {
      zIndex: REPERTOIRE_SELECT_MENU_Z_INDEX + 1,
      mt: '6px',
      color: '#ECECED',
      backgroundColor: '#10192E',
      border: '1px solid #3E4C73',
      borderRadius: '10px',
      boxShadow: '0px 12px 26px rgba(0, 0, 0, 0.38)',
      maxHeight: '260px',
      '& .MuiMenuItem-root': {
        minHeight: '36px',
        fontFamily: 'Lato',
        fontSize: '14px',
        fontWeight: 600,
      },
      '& .MuiMenuItem-root.Mui-selected': {
        backgroundColor: 'rgba(111, 135, 241, 0.32)',
      },
      '& .MuiMenuItem-root.Mui-selected:hover, & .MuiMenuItem-root:hover': {
        backgroundColor: 'rgba(111, 135, 241, 0.22)',
      },
    },
  },
} as const;

const repertoireAudioInputStatusCardSx = (tone: 'ready' | 'warning' | 'idle') => ({
  display: 'grid',
  gridTemplateColumns: '10px minmax(0, 1fr) auto',
  alignItems: 'center',
  gap: '10px',
  borderRadius: '14px',
  border:
    tone === 'ready'
      ? '1px solid rgba(94, 187, 130, 0.28)'
      : tone === 'warning'
        ? '1px solid rgba(255, 210, 125, 0.3)'
        : '1px solid rgba(226, 234, 255, 0.14)',
  backgroundColor:
    tone === 'ready'
      ? 'rgba(94, 187, 130, 0.12)'
      : tone === 'warning'
        ? 'rgba(255, 210, 125, 0.1)'
        : 'rgba(226, 234, 255, 0.06)',
  padding: '12px',
  marginLeft: '28px',
  marginRight: '28px',
  marginBottom: '12px',
});

const repertoireAudioInputDotSx = (tone: 'ready' | 'warning' | 'idle') => ({
  width: '10px',
  height: '10px',
  borderRadius: '999px',
  backgroundColor:
    tone === 'ready' ? '#5EBB82' : tone === 'warning' ? '#FFD27D' : 'rgba(226,234,255,0.42)',
  boxShadow:
    tone === 'ready'
      ? '0 0 0 4px rgba(94, 187, 130, 0.12)'
      : tone === 'warning'
        ? '0 0 0 4px rgba(255, 210, 125, 0.1)'
        : 'none',
});

const repertoireAudioInputTitleSx = {
  color: '#F2F6FF',
  fontSize: '13px',
  fontWeight: 900,
  lineHeight: 1.25,
};

const repertoireAudioInputDetailSx = {
  color: 'rgba(226, 234, 255, 0.64)',
  fontSize: '12px',
  fontWeight: 700,
  marginTop: '2px',
};

const repertoireAudioInputActionButtonSx = {
  borderRadius: '999px',
  borderColor: 'rgba(127, 215, 255, 0.34)',
  color: '#C7F0FF',
  fontSize: '11px',
  fontWeight: 900,
  minHeight: '28px',
  paddingX: '10px',
  textTransform: 'none',
  whiteSpace: 'nowrap',
  '& .MuiButton-startIcon': {
    marginRight: '4px',
    '& svg': {
      fontSize: '15px',
    },
  },
  '&:hover': {
    borderColor: 'rgba(127, 215, 255, 0.56)',
    backgroundColor: 'rgba(127, 215, 255, 0.1)',
  },
};

const repertoireAudioDiagnosticsButtonSx = {
  borderRadius: '999px',
  borderColor: 'rgba(118, 124, 140, 0.72)',
  color: '#ECECED',
  fontSize: '13px',
  fontWeight: 700,
  minHeight: '36px',
  paddingX: '14px',
  textTransform: 'none',
  '&:hover': {
    borderColor: '#6F87F1',
    backgroundColor: 'rgba(111, 135, 241, 0.08)',
  },
};

interface RepertoirePlayProps {
  setRenderRepertoirePlay: (value: boolean) => void;
}

const RepertoirePlay = (props: RepertoirePlayProps) => {
  const { setRenderRepertoirePlay: _setRenderRepertoirePlay } = props;
  const dispatch = useDispatch();
  const location = useLocation();
  // The header's Back; Android's hardware Back on the player does the same.
  const backToRepertoire = useRepertoirePlayBack();

  const data = useSelector((state: MainAppReducer) => state.mainAppReducer);
  const repertoireReducerData = useSelector((state: RepertoireReducer) => state.repertoireReducer);
  const repertoireData = repertoireReducerData.repertoireData;
  const repertoireIdSelect = repertoireReducerData.repertoireIdSelect;
  const userRepertoireProgress = repertoireReducerData.userRepertoireProgresses;
  const { repertoireEnded } = repertoireReducerData;
  const repertoireProgressSelected = useMemo(
    () => find(userRepertoireProgress, (urd) => urd?.repertoire?.id === repertoireIdSelect),
    [userRepertoireProgress, repertoireIdSelect],
  );
  const repertoireSelected = find(repertoireData, (rd) => rd.id === repertoireIdSelect);
  const [transposeSemitones, setTransposeSemitones] = useState(0);
  const [headerExtraHeightPx, setHeaderExtraHeightPx] = useState(0);
  const [modeConversion, setModeConversion] = useState<ParallelModeConversion>('off');
  const [showAccuracyDetails, setShowAccuracyDetails] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showAccuracy, setShowAccuracy] = useState(false);
  const graded = useEffectiveGrading();
  const [cursorIsVisible, setCursorIsVisible] = useState(true);
  const [zoomLevel, setZoomLevel] = useState(data.zoomLevel ?? 0.9);

  React.useEffect(() => {
    const zoom = Math.max(0.5, Math.min(zoomLevel, 1));
    NotifyPhaserEvents.SetZoomLevel({ zoomLevel: zoom });
    dispatch(appActions.setZoomLevel(zoom));
  }, [zoomLevel, dispatch]);
  const tempoInputRef = useRef<HTMLInputElement>(null);
  const [repTourOpen, setRepTourOpen] = useState(false);
  // Android's Back closes the accuracy and settings dropdowns as a tap outside them does. During
  // the tour the tour opens them, and a tap outside leaves them open, so Back does too.
  // They open from the player's header, drawn at z-index 1500.
  useHardwareBackClosesLayer(
    showAccuracyDetails && !repTourOpen,
    () => setShowAccuracyDetails(false),
    1500,
  );
  useHardwareBackClosesLayer(showSettings && !repTourOpen, () => setShowSettings(false), 1500);
  const {
    accuracyPercent,
    setAccuracy,
    showCursorSelector,
    setShowCursorSelector,
    performanceTempo,
    inPerformanceMode,
    setInPerformanceMode,
    softRefreshRepertoire,
    repertoirePlaybackMode,
    setRepertoirePlaybackMode,
  } = useRepertoirePlayContext();

  const goalTempo = resolveGoalTempo(performanceTempo);
  // MUS-29: pieces open at 70% of goal until the user sets a tempo of their own,
  // which persists as `last_tempo` and takes over from then on.
  const initialTempo = resolveInitialRepertoireTempo(
    repertoireProgressSelected?.last_tempo,
    goalTempo,
    Boolean(repertoireProgressSelected),
  );
  const startsBelowGoalTempo = initialTempo < goalTempo;

  const saveCurrentTempo = useCallback(
    (tempo: number) => {
      if (repertoireProgressSelected) {
        dispatch(
          repertoireActions.updateRepertoireDataLocal({
            id: repertoireProgressSelected.id,
            last_tempo: tempo,
          }),
        );
      }
    },
    [dispatch, repertoireProgressSelected],
  );

  const saveGoalTempo = useCallback((_tempo: number) => {}, []);

  const onTempoResetPersistence = useCallback(
    ({ currentTempo }: { currentTempo: number; goalTempo: number }) => {
      if (repertoireProgressSelected) {
        dispatch(
          repertoireActions.updateRepertoireDataLocal({
            id: repertoireProgressSelected.id,
            last_tempo: currentTempo,
          }),
        );
      }
    },
    [dispatch, repertoireProgressSelected],
  );

  const { userData } = data;
  const scoreTheme = normalizeScoreTheme(
    userData?.practice_score_theme,
    userData?.practice_dark_mode,
  );
  const unseenApptourFeatures: ApptourFeatureItem[] = data.unseenApptourFeatures ?? [];
  const [activeFeatureList, setActiveFeatureList] = useState<ApptourFeatureItem[]>([]);
  // Without a heard input: true while the tour shows only the "Not graded" tip.
  const [ungradedRepTourTipOnly, setUngradedRepTourTipOnly] = useState(false);
  const ungradedRepTourShownRef = useRef(false);
  // This block is required if a user is at end of repertoire then reloads the page manually, navigates back with buttons or browser, we want to reset this.
  useEffect(() => {
    dispatch(repertoireActions.setRepertoireEnded(false));
  }, [dispatch, location.pathname]);

  useEffect(() => {
    if (!inPerformanceMode || native) return;
    const handleMouseMove = (e: MouseEvent) => {
      if (e.clientY < 50) {
        setHeaderVisible(true);
      } else if (e.clientY > 120) {
        setHeaderVisible(false);
      }
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, [inPerformanceMode]);

  // const softRefreshRepertoire = useCallback(() => {
  //   // To "refresh" the page, we render this whole component false and then true again to remount everything.
  //   setRenderRepertoirePlay(false)
  //   // setTimeout(() => {
  //   //   setRenderRepertoirePlay(true)
  //   // }, 250)
  // }, [repertoireEnded])

  const [scrollPosition, setScrollPosition] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Wrapper to scroll to top and then soft refresh
  const handleSoftRefresh = useCallback(async () => {
    // Scroll to top immediately
    if (containerRef.current) {
      containerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
    await softRefreshRepertoire();
  }, [softRefreshRepertoire]);

  const handleTransposeChange = useCallback(
    (nextSemitones: number) => {
      if (nextSemitones === transposeSemitones) {
        return;
      }
      console.log(
        '[LOOP-DBG] setRepertoirePlaying(false) from RepertoirePlay handleTransposeChange',
      );
      dispatch(repertoireActions.setRepertoirePlaying({ repertoirePlaying: false }));
      dispatch(repertoireActions.setRepertoireEnded(false));
      setTransposeSemitones(nextSemitones);
    },
    [dispatch, transposeSemitones],
  );

  const handleModeConversionChange = useCallback(
    (nextMode: ParallelModeConversion) => {
      if (nextMode === modeConversion) {
        return;
      }
      console.log(
        '[LOOP-DBG] setRepertoirePlaying(false) from RepertoirePlay handleModeConversionChange',
      );
      dispatch(repertoireActions.setRepertoirePlaying({ repertoirePlaying: false }));
      dispatch(repertoireActions.setRepertoireEnded(false));
      setModeConversion(nextMode);
    },
    [dispatch, modeConversion],
  );

  const playLevel = useCallback(() => {
    const willStart = repertoireEnded || !repertoireReducerData.repertoirePlaying;
    if (willStart && repertoirePlaybackModePlaysNotes(repertoirePlaybackMode)) {
      void prepareRepertoireNotePlaybackAudio();
    }

    dispatch(appActions.setPlayOnStart(false));
    console.log('playing level', repertoireEnded);
    if (repertoireEnded) {
      // setRenderRepertoirePlay(false)
      setTimeout(async () => {
        // setRenderRepertoirePlay(true)
        // IMPORTANT: await handleSoftRefresh to ensure scheduler is fully reset
        // before starting playback (also scrolls to top)
        await handleSoftRefresh();
        dispatch(repertoireActions.setRepertoireEnded(false));
        dispatch(appActions.setPlayOnStart(true));
        console.log('setting repertoire playing to true', !repertoireReducerData.repertoirePlaying);
        dispatch(
          repertoireActions.setRepertoirePlaying({
            repertoirePlaying: !repertoireReducerData.repertoirePlaying,
          }),
        );
      }, 250);
    } else {
      dispatch(
        repertoireActions.setRepertoirePlaying({
          repertoirePlaying: !repertoireReducerData.repertoirePlaying,
        }),
      );
    }
  }, [
    dispatch,
    repertoirePlaybackMode,
    repertoireReducerData.repertoirePlaying,
    repertoireEnded,
    handleSoftRefresh,
  ]);

  const lastSpacePressRef = useRef(0);
  // Need to prevent space (play) from scrolling down lol.
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.code === 'Space') {
        event.preventDefault();
        const now = Date.now();
        if (!repertoireEnded && now - lastSpacePressRef.current < 300) {
          // Double-tap detected
          handleSoftRefresh();
        } else {
          playLevel();
        }
        lastSpacePressRef.current = now;
      } else if (event.key === 'r' || event.key === 'R') {
        handleSoftRefresh();
      } else if (event.key === 'Enter') {
        if (tempoInputRef.current) {
          tempoInputRef.current.blur();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [repertoireReducerData.repertoirePlaying, repertoireEnded, handleSoftRefresh, playLevel]);

  // Mobile: the scene requests a restart when the user double-taps the score
  // after the piece has finished. Run the canonical soft refresh (clears
  // repertoireEnded, resets the scene, auto-starts the count-in) — the same
  // path as the desktop 'r' key and the on-screen restart button — instead of
  // letting the scene resume playback at the last note.
  useEffect(() => {
    PhaserToReactListeners.RepertoireRestartRequested(() => {
      void handleSoftRefresh();
    });
    return () => {
      RemoveListener('RepertoireRestartRequested');
    };
  }, [handleSoftRefresh]);

  const native = isCapacitorNative();
  const {
    headerVisible,
    setHeaderVisible,
    showHeaderTemporarily: _showHeaderTemporarily,
    hideHeaderSoon,
  } = useTheaterUI({
    isNative: native,
    inPerformanceMode,
    containerRef,
  });
  const togglePerformanceMode = useCallback(() => {
    setInPerformanceMode((prev: boolean) => {
      const next = !prev;
      setHeaderVisible(!next ? true : false);
      return next;
    });
  }, [setInPerformanceMode, setHeaderVisible]);

  const handleRepertoirePlaybackModeChange = useCallback(
    (nextMode: RepertoirePlaybackMode) => {
      if (nextMode === repertoirePlaybackMode) return;
      if (repertoireReducerData.repertoirePlaying) {
        dispatch(repertoireActions.setRepertoirePlaying({ repertoirePlaying: false }));
      }
      setRepertoirePlaybackMode(nextMode);
    },
    [
      dispatch,
      repertoirePlaybackMode,
      repertoireReducerData.repertoirePlaying,
      setRepertoirePlaybackMode,
    ],
  );

  useEffect(() => {
    if (containerRef.current && scrollPosition != null) {
      // setTimeout(() => {
      if (containerRef.current) {
        containerRef.current.scrollTo({ top: scrollPosition, behavior: 'smooth' });
      }
      // }, 2000)
    }
  }, [scrollPosition]);

  useEffect(() => {
    if (graded && !userData?.repertoire_tutorial_seenat) {
      setTimeout(() => {
        setRepTourOpen(true);
      }, 500);
    }
  }, []);

  const mobileScreen = useMediaQuery('(max-width: 960px)');

  // Auto-hiding the header during playback is a phone-only behavior. Key it off
  // device type, not viewport width — an iPad in portrait is narrower than
  // 960px but must keep its header visible while playing. (mobileScreen stays
  // width-based for responsive sizing, where compacting on a narrow iPad is
  // still desired.)
  const isPhoneDevice = getDeviceClass() === 'phone';

  useEffect(() => {
    if (!isPhoneDevice) return;
    setHeaderVisible(!repertoireReducerData.repertoirePlaying);
  }, [isPhoneDevice, repertoireReducerData.repertoirePlaying, setHeaderVisible]);

  // MUS-29: explains the 70% starting tempo, anchored on the "Below Goal Tempo"
  // red dot. Only shown while the piece actually sits below goal — once the user
  // has raised the tempo themselves the copy no longer describes what they see.
  const startingTempoStep: AppTourStep | undefined = startsBelowGoalTempo
    ? {
        targetId: 'rep-tempo-goal-indicator',
        title: 'Starting Tempo',
        content: 'All songs start at 70% of their goal tempo. Raise tempo to increase difficulty.',
        position: TooltipRelativePosition.RIGHT,
        preStepFunction: () => {
          NotifyReactEvents.AppTourHelper({ step: AppTourStepHelper.ExpandTempoControls });
        },
        postStepFunction: () => {
          NotifyReactEvents.AppTourHelper({ step: AppTourStepHelper.EndTour });
        },
      }
    : undefined;

  const RepertoireTourSteps: AppTourStep[] = [
    {
      targetId: 'rep-play-button',
      title: 'Play / Pause',
      content: native
        ? 'Tap to start or pause playback.'
        : 'Tap to start or pause playback. Double-press space to restart.',
      position: TooltipRelativePosition.LEFT,
    },
    {
      targetId: 'rep-performance-mode',
      title: 'Performance Mode',
      content: 'Toggle performance mode for a clean, focused view during play.',
      position: TooltipRelativePosition.RIGHT,
    },
    {
      targetId: 'rep-tempo-container',
      title: 'Tempo Controls',
      content:
        'Open the control panel to adjust BPM with the slider or +/- buttons, reset to goal tempo.',
      position: TooltipRelativePosition.RIGHT,
      preStepFunction: () => {
        NotifyReactEvents.AppTourHelper({ step: AppTourStepHelper.ExpandTempoControls });
      },
      // The tempo panel stays open for the starting-tempo step when it is
      // present; that step then collapses it on the way out.
      postStepFunction: startingTempoStep
        ? undefined
        : () => {
            NotifyReactEvents.AppTourHelper({ step: AppTourStepHelper.EndTour });
          },
    },
    ...(startingTempoStep ? [startingTempoStep] : []),
    {
      targetId: 'rep-accuracy-dropdown',
      title: 'Accuracy Feedback',
      content: 'Toggle accuracy feedback on/off for each hand.',
      position: TooltipRelativePosition.LEFT,
      preStepFunction: () => {
        NotifyReactEvents.AppTourHelper({ step: AppTourStepHelper.EndTour });
        setShowAccuracyDetails(true);
      },
      postStepFunction: () => {
        setShowAccuracyDetails(false);
      },
    },
    {
      targetId: 'rep-looping-button',
      title: 'Looping',
      content:
        'Enable looping to select a section of the piece to practice. Drag the bracket handles to adjust the loop range.',
      position: TooltipRelativePosition.LEFT,
      feature: ApptourFeature.REPERTOIRE_LOOPING,
    },
  ];

  // Filter unseen features to only those that have a matching step in this tour
  const unseenRepertoireFeatures = unseenApptourFeatures.filter((f) =>
    RepertoireTourSteps.some((step) => step?.feature === f.key),
  );
  const hasUnseenRepertoireFeatures = unseenRepertoireFeatures.length > 0;

  // Without a heard input the accuracy step is replaced by the "Not graded"
  // tip, tracked under its own key: the tour opens on its own while that tip
  // is unseen (whole for a player who never had it, else the tip alone) and
  // never sets repertoire_tutorial_seenat.
  const ungradedRepertoireTourSteps = withUngradedRepertoireAccuracy(
    RepertoireTourSteps,
    notGradedRepertoireStep,
  );
  const ungradedRepTourDue = graded
    ? 'none'
    : ungradedRepertoireTourDue({
        repertoireTourSeen: !!userData?.repertoire_tutorial_seenat,
        unseenFeatures: unseenApptourFeatures,
        shownThisSession: ungradedRepTourShownRef.current,
      });
  useEffect(() => {
    if (ungradedRepTourDue === 'none' || repTourOpen) return;
    const timeout = setTimeout(() => {
      setUngradedRepTourTipOnly(ungradedRepTourDue === 'tip');
      setRepTourOpen(true);
    }, 500);
    return () => clearTimeout(timeout);
  }, [ungradedRepTourDue, repTourOpen]);
  const ungradedRepTourOpenSteps = ungradedRepTourTipOnly
    ? [notGradedRepertoireStep]
    : ungradedRepertoireTourSteps;
  // Which tour is open is fixed when it opens: the input can change under it.
  const repTourGraded = useLatchedWhileOpen(repTourOpen, graded);
  useEffect(() => {
    if (!repTourOpen || repTourGraded) return;
    ungradedRepTourShownRef.current = true;
    const features = featuresToMarkSeen(ungradedRepTourOpenSteps, unseenApptourFeatures);
    if (features.length > 0) dispatch(appActions.markApptourFeaturesAsSeen(features));
  }, [repTourOpen]);

  if (!repertoireSelected) {
    return <></>;
  }

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', height: '100vh', backgroundColor: 'white' }}
    >
      {/* {native && inPerformanceMode && !headerVisible && (
        <>
          <TopRevealZone onReveal={() => showHeaderTemporarily()} />
          <FloatingHeaderToggle
            onToggle={() => {
              // manual toggle: if opening, auto-hide in 3s
              if (!headerVisible) showHeaderTemporarily();
              else setHeaderVisible(false);
            }}
          />
        </>
      )} */}

      <TempoProvider
        performanceTempo={goalTempo}
        initialTempo={initialTempo}
        initialGoalTempo={150}
        parentContext="repertoire"
        saveCurrentTempo={saveCurrentTempo}
        saveGoalTempo={saveGoalTempo}
        onTempoResetPersistence={onTempoResetPersistence}
      >
        <Box sx={{ position: 'fixed', width: '100%', zIndex: 1500 }}>
          <Header
            onBack={backToRepertoire}
            onWrappedExtraHeightChange={setHeaderExtraHeightPx}
            mobileScreen={mobileScreen}
            repTourOpen={repTourOpen}
            showAccuracy={showAccuracy}
            // Ungraded play shows no accuracy: the readout stays blank.
            accuracyPercent={graded ? accuracyPercent : null}
            showCursorSelector={showCursorSelector}
            setShowCursorSelector={setShowCursorSelector}
            headerVisible={headerVisible}
            togglePerformanceMode={togglePerformanceMode}
            inPerformanceMode={inPerformanceMode}
            showAccuracyDetails={showAccuracyDetails}
            setShowAccuracyDetails={setShowAccuracyDetails}
            showSettings={showSettings}
            setShowSettings={setShowSettings}
            tempoInputRef={tempoInputRef}
            setCursorIsVisible={setCursorIsVisible}
            cursorIsVisible={cursorIsVisible}
            transposeSemitones={transposeSemitones}
            onTransposeChange={handleTransposeChange}
            modeConversion={modeConversion}
            onModeConversionChange={handleModeConversionChange}
            showTransposeControl={true}
            zoomLevel={zoomLevel}
            setZoomLevel={setZoomLevel}
            repertoirePlaybackMode={repertoirePlaybackMode}
            onRepertoirePlaybackModeChange={handleRepertoirePlaybackModeChange}
          />
        </Box>
      </TempoProvider>
      <Box
        className="main-app-container-styles"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1, // fill remaining viewport height (header & footer are position:fixed)
          minHeight: 0, // allow flex child to shrink below content size
          alignContent: 'center',
          alignItems: 'center',
          paddingTop: '0px', //'170px', // since making header fixed position
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
        ref={containerRef}
      >
        {/* <Box sx={{position: 'absolute', left: '10px', top: '150px'}}>
          ON: {capMidiReceived.on}
          <br/>
          OFF: {capMidiReceived.off}
          <br/>
          Other: {capMidiReceived.other}
          <br/>
          connected: {`${capMidiReceived.connected}!`}
        </Box> */}
        {/* Title/composer are rendered inside the Phaser scene so they scroll
            with the music. */}
        <RepertoirePlayContainerOSMD
          key={`rep-osmd-${transposeSemitones}-${modeConversion}-${scoreTheme}`}
          showKeyboard={false}
          setAccuracy={setAccuracy}
          setShowAccuracy={setShowAccuracy}
          cursorIsVisible={cursorIsVisible}
          transposeSemitones={transposeSemitones}
          modeConversion={modeConversion}
          scrollPosition={scrollPosition}
          setScrollPosition={setScrollPosition}
          headerVisible={headerVisible}
          headerExtraHeightPx={headerExtraHeightPx}
        />
      </Box>
      {/* Bottom-left info button to launch tour */}
      <FooterContainer
        mobileScreen={mobileScreen}
        scoreTheme={scoreTheme}
        headerVisible={headerVisible}
        // A phone hides the header while the piece plays; Play / Pause stays
        // on screen so the piece can always be paused.
        keepTransportVisible={isPhoneDevice && !inPerformanceMode}
        playLevel={playLevel}
        softRefreshRepertoire={handleSoftRefresh}
        afterPrimaryAction={() => {
          if (native && inPerformanceMode) {
            if (!repertoireReducerData.repertoirePlaying) {
              hideHeaderSoon(200);
            }
          }
        }}
        onShowInfo={() => setRepTourOpen(true)}
      />
      <PlayAlongIdlePause
        playing={repertoireReducerData.repertoirePlaying}
        pause={() => dispatch(repertoireActions.setRepertoirePlaying({ repertoirePlaying: false }))}
        resume={() => resumeAfterIdlePause('repertoire', playLevel)}
      />
      {/* App tour component */}
      <AppTour
        skipIntro={true}
        steps={
          !repTourGraded
            ? ungradedRepTourOpenSteps
            : activeFeatureList.length > 0
              ? filterByUnseenFeatures(RepertoireTourSteps, activeFeatureList)
              : RepertoireTourSteps
        }
        isOpen={repTourOpen}
        onStepShown={(step, stepNumber, stepCount) =>
          reportTooltipSeen({
            surface: 'repertoire',
            graded: repTourGraded,
            step,
            stepNumber,
            stepCount,
          })
        }
        onClose={() => {
          if (repTourGraded && !userData?.repertoire_tutorial_seenat) {
            dispatch(
              appActions.updateUserData({
                repertoire_tutorial_seenat: new Date(),
              }),
            );
          }
          setRepTourOpen(false);
          setActiveFeatureList([]);
          setUngradedRepTourTipOnly(false);
        }}
        onStepChange={() => {}}
        tooltipClassName="tour-tooltip"
        onSkipTour={() => {
          if (repTourGraded && !userData?.repertoire_tutorial_seenat) {
            dispatch(
              appActions.updateUserData({
                repertoire_tutorial_seenat: new Date(),
              }),
            );
          }
          setRepTourOpen(false);
          setActiveFeatureList([]);
          setUngradedRepTourTipOnly(false);
        }}
      />
    </Box>
  );
};

const accuracyColors = [
  '#00C2FF', //neutral
  '#40E16D', // green
  '#FFBF00', // yellow
  '#ff7d6f',
  // 'rgba(200, 128, 114, 1.0)',// '#EB3459', // red
];

// Perfect Phrase Counter bounds. The user can require between 2 and 4
// consecutive perfect runs of a phrase before the loop is "perfected". The
// chosen value is persisted in Redux (defaults to 3, see repertoireReducer)
// and forwarded to the Phaser scene via SetPerfectPhraseConfig.
const PERFECT_PHRASE_MIN = 2;
const PERFECT_PHRASE_MAX = 4;

/**
 * Looping settings popup anchored under the loop pill. Renders the
 * "LOOPING" header plus the Perfect Phrase Counter row (enable/disable
 * toggle on the left, increment/decrement counter on the right).
 *
 * State lives in Redux (read/written by LoopingToggleButton); the container
 * forwards the values to the Phaser scene via SetPerfectPhraseConfig.
 */
interface LoopingMenuProps {
  perfectPhraseEnabled: boolean;
  setPerfectPhraseEnabled: (value: boolean) => void;
  perfectPhraseCount: number;
  onAdjustCount: (delta: number) => void;
  anchorSx: Record<string, unknown>;
}

const LoopingMenu: React.FC<LoopingMenuProps> = ({
  perfectPhraseEnabled,
  setPerfectPhraseEnabled,
  perfectPhraseCount,
  onAdjustCount,
  anchorSx,
}) => {
  // The plus/minus SVGs are full button graphics; render them directly as
  // images and convey the disabled state by dimming and disabling clicks.
  const counterButtonSx = (disabled: boolean) =>
    ({
      width: 28,
      height: 28,
      display: 'block',
      userSelect: 'none',
      cursor: disabled ? 'default' : 'pointer',
      opacity: disabled ? 0.4 : 1,
      pointerEvents: disabled ? 'none' : 'auto',
    }) as const;

  const decrementDisabled = !perfectPhraseEnabled || perfectPhraseCount <= PERFECT_PHRASE_MIN;
  const incrementDisabled = !perfectPhraseEnabled || perfectPhraseCount >= PERFECT_PHRASE_MAX;

  return (
    <Box
      id="rep-looping-menu"
      sx={{
        // Anchored 5px below the header bottom, centred on the loop toggle
        // (see useHeaderPopupAnchor).
        ...anchorSx,
        zIndex: 20,
        minWidth: '380px',
        width: 'min(420px, calc(100vw - 32px))',
        borderRadius: '8px',
        boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.25)',
        padding: '16px 24px 24px',
        backgroundColor: '#050B13',
        // Unique keyframe name: Emotion does not scope @keyframes names, so a
        // shared `fadeInSlideDown` collides with the other popups' definitions
        // (last inserted wins). Those other definitions animate `translateY`
        // only, which would drop this menu's `translateX(-50%)` centering for
        // the animation's duration — the menu opens offset to the right and
        // then snaps to centre when the animation ends.
        animation: 'loopMenuFadeInSlideDown 0.2s ease-out',
        '@keyframes loopMenuFadeInSlideDown': {
          '0%': { opacity: 0, transform: 'translate(-50%, -10px)' },
          '100%': { opacity: 1, transform: 'translate(-50%, 0)' },
        },
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: '-10px',
          left: '50%',
          marginLeft: '-16px',
          width: '0',
          height: '0',
          borderLeft: '16px solid transparent',
          borderRight: '16px solid transparent',
          borderBottom: '16px solid #050B13',
        }}
      />
      <Box
        sx={{
          ...detailsTitleTextStyles,
          textAlign: 'center',
          marginTop: '6px',
          marginBottom: '20px',
          fontSize: '20px',
        }}
      >
        LOOPING
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          color: '#FFFFFF',
          fontFamily: 'Lato',
        }}
      >
        <CustomSwitch
          checked={perfectPhraseEnabled}
          onChange={() => setPerfectPhraseEnabled(!perfectPhraseEnabled)}
          inputProps={{ 'aria-label': 'Toggle perfect phrase counter' }}
        />
        <Box component="span" sx={{ flex: 1, fontSize: '17px', fontWeight: 700 }}>
          Perfect Phrase Counter
        </Box>
        <Box
          component="img"
          src={MinusButton}
          role="button"
          aria-label="Decrease perfect phrase counter"
          aria-disabled={decrementDisabled}
          onClick={() => {
            if (decrementDisabled) return;
            onAdjustCount(-1);
          }}
          sx={counterButtonSx(decrementDisabled)}
        />
        <Box
          sx={{
            minWidth: '20px',
            textAlign: 'center',
            fontSize: '20px',
            fontWeight: 700,
            opacity: perfectPhraseEnabled ? 1 : 0.5,
          }}
        >
          {perfectPhraseCount}
        </Box>
        <Box
          component="img"
          src={PlusButton}
          role="button"
          aria-label="Increase perfect phrase counter"
          aria-disabled={incrementDisabled}
          onClick={() => {
            if (incrementDisabled) return;
            onAdjustCount(1);
          }}
          sx={counterButtonSx(incrementDisabled)}
        />
      </Box>
    </Box>
  );
};

/**
 * Looping toggle button for the repertoire play header.
 *
 * The loop icon (inner circular ring) toggles `isLoopingActive` in the
 * RepertoirePlayContext. The surrounding pill + downward chevron is a
 * separate hit target that opens the LOOPING settings popup.
 */
const LoopingToggleButton: React.FC<{ sp: (n: number) => string }> = ({ sp }) => {
  const { isLoopingActive, setIsLoopingActive } = useRepertoirePlayContext();
  const dispatch = useDispatch();

  const [showLoopingMenu, setShowLoopingMenu] = useState(false);
  // Android's Back closes the popup as a tap outside it does.
  // It opens from the player's header, drawn at z-index 1500.
  useHardwareBackClosesLayer(showLoopingMenu, () => setShowLoopingMenu(false), 1500);
  // Perfect Phrase Counter state lives in Redux so the Phaser scene can source
  // the qualifying streak (the container forwards it via SetPerfectPhraseConfig).
  const perfectPhraseEnabled = useSelector(selectPerfectPhraseEnabled);
  const perfectPhraseCount = useSelector(selectPerfectPhraseCount);

  const wrapperRef = useRef<HTMLDivElement>(null);
  // Anchor the popup 5px below the header, horizontally centred on the loop pill.
  const anchorSx = useHeaderPopupAnchor(wrapperRef, showLoopingMenu, 'center');

  const setPerfectPhraseEnabled = (value: boolean) => {
    dispatch(repertoireActions.setPerfectPhraseEnabled(value));
  };

  const adjustCount = (delta: number) => {
    const next = Math.min(
      PERFECT_PHRASE_MAX,
      Math.max(PERFECT_PHRASE_MIN, perfectPhraseCount + delta),
    );
    if (next !== perfectPhraseCount) {
      dispatch(repertoireActions.setPerfectPhraseCount(next));
    }
  };

  // Close the popup when interacting outside the loop pill / popup. Mirrors
  // the header's outside-interaction pattern (capture-phase pointer events
  // with stable targets) so it dismisses alongside the accuracy/settings
  // dropdowns.
  useEffect(() => {
    if (!showLoopingMenu) return;
    const handleOutside = (event: Event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (target && wrapperRef.current && !wrapperRef.current.contains(target)) {
        setShowLoopingMenu(false);
      }
    };
    const events = ['pointerdown', 'touchstart', 'mousedown'] as const;
    events.forEach((name) => document.addEventListener(name, handleOutside, true));
    return () => {
      events.forEach((name) => document.removeEventListener(name, handleOutside, true));
    };
  }, [showLoopingMenu]);

  const handleToggleLooping = () => {
    const nextLooping = !isLoopingActive;
    setIsLoopingActive(nextLooping);
    // MUS-243: turning looping OFF transitions the machine to `stopped`
    // (USER_TOGGLE_LOOP → .stopped). The legacy `repertoirePlaying` boolean
    // — which drives the play/pause button icon and the Redux→EventBus
    // play/pause bridge — is intentionally NOT mirrored from the machine
    // phase (avoids a re-render loop). Without this it stays stale-true from
    // the loop playback: the button shows "pause" and the user needs two
    // space presses (one wasted toggling the stale flag) before the count-in
    // starts. Resetting it on this explicit user gesture keeps the legacy
    // boolean in sync with the machine's stopped state.
    if (!nextLooping) {
      dispatch(repertoireActions.setRepertoirePlaying({ repertoirePlaying: false }));
    }
  };

  return (
    <Box ref={wrapperRef} sx={{ position: 'relative', display: 'flex' }}>
      <Box
        data-rep-toggle="looping"
        // The pill is an interactive settings target that opens the looping
        // (Perfect Phrase Counter) popup. The loop icon inside remains its own
        // toggle button below.
        role="button"
        aria-label="Looping settings"
        aria-expanded={showLoopingMenu}
        tabIndex={0}
        // Clicking anywhere on the pill (background / chevron) — but NOT the
        // loop icon itself, which stops propagation below — opens the popup.
        onClick={() => setShowLoopingMenu((prev) => !prev)}
        onKeyDown={(event: React.KeyboardEvent) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          setShowLoopingMenu((prev) => !prev);
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          // The pill wraps the loop disc + chevron.
          gap: sp(2),
          // Equal inset on the top/bottom/left so the pill's rounded left cap is
          // concentric with the circular loop disc and hugs it with an even
          // margin (with zero vertical padding the cap radius matched the disc
          // radius but sat off-centre, so the disc poked out top/bottom-left). A
          // touch more padding on the right makes room for the chevron.
          paddingTop: sp(4),
          paddingBottom: sp(4),
          paddingLeft: sp(4),
          paddingRight: sp(10),
          borderRadius: '999px',
          backgroundColor: 'rgba(63, 81, 132, 0.45)',
          cursor: 'pointer',
          transition: 'background-color 0.2s',
          '&:hover': { backgroundColor: 'rgba(63, 81, 132, 0.7)' },
        }}
      >
        <Box
          sx={{
            ...controlsStyles,
            // Match the accuracy/tempo icons: scale the footprint by the header
            // fit factor (`sp`) instead of hardcoding pixels, so the loop disc
            // stays the same size as the neighbouring controls when the header
            // shrinks. Fixed square footprint (height + width) so the inline
            // loop glyph can't reflow the ring (and therefore the pill) after
            // first paint — otherwise the dropdown, which centres on the pill,
            // jumps once the icon settles.
            height: sp(54),
            width: sp(54),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            // When looping is active, render a clean solid blue disc like the
            // tempo icon. The grey ring is made transparent rather than removed
            // so the 4px border box is preserved (same footprint); since
            // background-clip defaults to border-box, the fill bleeds through
            // the transparent border for a borderless solid disc.
            ...(isLoopingActive ? { backgroundColor: '#6F87F1', borderColor: 'transparent' } : {}),
          }}
        >
          <IconButton
            id="rep-looping-button"
            onClick={(event) => {
              // The loop icon toggles looping and must NOT open the popup,
              // so swallow the click before it reaches the pill handler.
              event.stopPropagation();
              handleToggleLooping();
            }}
            sx={{
              color: isLoopingActive ? '#FFFFFF' : '#ECECED',
              padding: sp(12),
              '&:hover': {
                backgroundColor: 'transparent',
              },
            }}
            aria-label={isLoopingActive ? 'Disable looping' : 'Enable looping'}
          >
            <Box
              component={LoopGlyph}
              sx={{
                // The glyph's stroke is hardcoded white; override it so the
                // icon reads white on the solid blue disc when active, grey
                // when inactive.
                width: sp(28),
                height: sp(28),
                display: 'block',
                '& path': {
                  stroke: isLoopingActive ? '#FFFFFF' : '#ECECED',
                },
              }}
            />
          </IconButton>
        </Box>
        <KeyboardArrowDownIcon
          sx={{
            color: '#ECECED',
            fontSize: 22,
            transition: 'transform 0.2s',
            transform: showLoopingMenu ? 'rotate(180deg)' : 'none',
          }}
        />
      </Box>
      {showLoopingMenu && (
        <LoopingMenu
          perfectPhraseEnabled={perfectPhraseEnabled}
          setPerfectPhraseEnabled={setPerfectPhraseEnabled}
          perfectPhraseCount={perfectPhraseCount}
          onAdjustCount={adjustCount}
          anchorSx={anchorSx}
        />
      )}
    </Box>
  );
};

interface HeaderProps {
  onBack: () => void;
  // How much taller than one row the header is while it wraps (0 otherwise).
  onWrappedExtraHeightChange?: (extraPx: number) => void;
  accuracyPercent: number | null | undefined;
  showAccuracy: boolean;
  showCursorSelector: boolean;
  setShowCursorSelector: (x: boolean) => void;
  headerVisible: boolean;
  togglePerformanceMode: () => void;
  inPerformanceMode: boolean;
  showAccuracyDetails: boolean;
  setShowAccuracyDetails: (value: boolean) => void;
  showSettings: boolean;
  setShowSettings: (value: boolean) => void;
  tempoInputRef: React.RefObject<HTMLInputElement | null>;
  cursorIsVisible: boolean;
  repTourOpen: boolean;
  transposeSemitones: number;
  showTransposeControl: boolean;
  onTransposeChange: (nextSemitones: number) => void;
  modeConversion: ParallelModeConversion;
  onModeConversionChange: (nextMode: ParallelModeConversion) => void;
  repertoirePlaybackMode: RepertoirePlaybackMode;
  onRepertoirePlaybackModeChange: (nextMode: RepertoirePlaybackMode) => void;
  setCursorIsVisible: (x: boolean) => void;
  zoomLevel: number;
  setZoomLevel: (zoomLevel: number) => void;
  mobileScreen: boolean;
}

const Header = (props: HeaderProps) => {
  const {
    onBack,
    onWrappedExtraHeightChange,
    mobileScreen,
    repTourOpen,
    accuracyPercent,
    headerVisible,
    togglePerformanceMode,
    inPerformanceMode,
    showAccuracyDetails,
    setShowAccuracyDetails,
    showSettings,
    setShowSettings,
    tempoInputRef,
    setCursorIsVisible,
    cursorIsVisible,
    transposeSemitones,
    showTransposeControl,
    onTransposeChange,
    modeConversion,
    onModeConversionChange,
    zoomLevel,
    setZoomLevel,
    repertoirePlaybackMode,
    onRepertoirePlaybackModeChange,
  } = props;

  const repertoirePlaybackModesEnabled = useFeatureFlag(FeatureFlag.USE_REPERTOIRE_PLAYBACK_MODES);
  const listenOnlyPlayback = repertoirePlaybackModesEnabled && repertoirePlaybackMode === 'listen';
  const listenAccent = getRepertoirePlaybackModeAccent('listen');
  // Without a heard input there is no accuracy to show or set: the accuracy
  // control is greyed out and does nothing (it is not a switch).
  const graded = useEffectiveGrading();
  const toggleAccuracyDetails = () => {
    if (graded) setShowAccuracyDetails(!showAccuracyDetails);
  };
  const tempoContainerRef = useRef<HTMLDivElement>(null);
  const tempoToggleRef = useRef<HTMLDivElement>(null);
  const accuracyContainerRef = useRef<HTMLDivElement>(null);
  const accuracyToggleRef = useRef<HTMLDivElement>(null);
  const settingsContainerRef = useRef<HTMLDivElement>(null);
  const settingsToggleRef = useRef<HTMLDivElement>(null);
  const settingsInteractionStartedInsideRef = useRef(false);

  // Anchor the accuracy/settings popups 5px below the header bottom, aligned to
  // their toggles (left-aligned for accuracy, right-aligned for settings).
  const accuracyAnchorSx = useHeaderPopupAnchor(
    accuracyToggleRef,
    showAccuracyDetails,
    'left',
    -24,
  );
  const settingsAnchorSx = useHeaderPopupAnchor(settingsToggleRef, showSettings, 'right', -12);

  // The header controls are sized for landscape. On the repertoire play page —
  // the only page allowed to rotate to portrait on a tablet — the control
  // cluster is wider than the viewport and clips the accuracy readout. Measure
  // the cluster against the space available beside the back button and shrink
  // the header's dimensions proportionally until it fits. This is reflow-based
  // (we scale the dimensions themselves, not via transform/zoom) so the
  // fixed-position tempo dropdown nested inside keeps its positioning.
  const headerRowRef = useRef<HTMLDivElement>(null);
  const headerClusterRef = useRef<HTMLDivElement>(null);
  const headerBackRef = useRef<HTMLDivElement>(null);
  const headerContainerRef = useRef<HTMLDivElement>(null);
  const [headerFit, setHeaderFit] = useState<RepertoireHeaderFit>(REPERTOIRE_HEADER_ONE_ROW);
  const headerAccuracyPillRef = useRef<HTMLDivElement>(null);
  const [wrappedHeaderHeightPx, setWrappedHeaderHeightPx] = useState(0);
  const [wrappedPillShiftPx, setWrappedPillShiftPx] = useState(0);
  const headerScale = headerFit.scale;
  const headerWrapped = headerFit.wrapped;
  const headerCompact = headerFit.compact;

  // MUS-607: tell the page how much lower the score must start under a wrapped
  // header (see repertoireScoreExtraTopPx); one-row headers report 0.
  useEffect(() => {
    onWrappedExtraHeightChange?.(
      repertoireScoreExtraTopPx({
        wrapped: headerWrapped,
        headerHeightPx: wrappedHeaderHeightPx,
        narrowWindow: mobileScreen,
        phoneLayout: getDeviceClass() === 'phone',
      }),
    );
  }, [headerWrapped, wrappedHeaderHeightPx, mobileScreen, onWrappedExtraHeightChange]);

  useLayoutEffect(() => {
    const measure = () => {
      const row = headerRowRef.current;
      const cluster = headerClusterRef.current;
      if (!row || !cluster) return;
      const rowRect = row.getBoundingClientRect();
      const backRight = headerBackRef.current
        ? headerBackRef.current.getBoundingClientRect().right
        : rowRect.left;
      const SAFETY_GAP = 16;
      const available = rowRect.right - backRight - SAFETY_GAP;
      // scrollWidth reflects the full content width even when it overflows.
      const controlsRight = cluster.getBoundingClientRect().left + cluster.scrollWidth;
      const next = nextRepertoireHeaderFit(
        headerFit,
        available,
        cluster.scrollWidth,
        controlsRight > rowRect.right + 0.5,
        repertoireHeaderHasRoomForTwoRows(window.innerHeight),
      );
      if (next !== headerFit) setHeaderFit(next);
      const container = headerContainerRef.current;
      const pill = headerAccuracyPillRef.current;
      if (next.wrapped && container && pill) {
        setWrappedHeaderHeightPx(container.offsetHeight);
        // While the header is away the readout stays on screen where it sits
        // in a one-row header, not over the score on its second-row spot.
        // (offsetTop ignores the slide transforms.)
        const oneRowHeight = repertoireHeaderOneRowHeightPx(mobileScreen);
        const oneRowTop = Math.round((oneRowHeight - pill.offsetHeight) / 2);
        setWrappedPillShiftPx(container.offsetHeight - pill.offsetTop + oneRowTop);
      }
    };
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('orientationchange', measure);
    // The controls can change width after the first measure (the web font
    // arriving, the tempo text): measure again then, or the row could stay
    // past the edge at the size it settled on before.
    const cluster = headerClusterRef.current;
    const observer =
      cluster && typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (cluster) observer?.observe(cluster);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('orientationchange', measure);
      observer?.disconnect();
    };
  }, [
    headerFit,
    mobileScreen,
    accuracyPercent,
    listenOnlyPlayback,
    showAccuracyDetails,
    showSettings,
  ]);

  const [_isSmallScreen, setIsSmallScreen] = useState(false); //useState(window.innerWidth <= smallScreenBreakWidth || window.innerHeight <= smallScreenBreakHeight);
  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(false);
    }; //window.innerWidth <= smallScreenBreakWidth || window.innerHeight <= smallScreenBreakHeight); }
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    const handleOutsideInteraction = (event: MouseEvent | PointerEvent | TouchEvent) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      // Ignore clicks on MUI select dropdown surfaces/options so Settings
      // doesn't close before the Select onChange can fire.
      const isSelectMenuInteraction = !!target.closest(REPERTOIRE_SELECT_MENU_INTERACTION_SELECTOR);
      const isSettingsInteraction =
        isSelectMenuInteraction ||
        !!settingsContainerRef.current?.contains(target) ||
        !!settingsToggleRef.current?.contains(target);

      if (
        event.type === 'pointerdown' ||
        event.type === 'touchstart' ||
        event.type === 'mousedown'
      ) {
        settingsInteractionStartedInsideRef.current = isSettingsInteraction;
      }

      const shouldIgnoreSettingsClose =
        event.type === 'click' && settingsInteractionStartedInsideRef.current;
      if (event.type === 'click') {
        settingsInteractionStartedInsideRef.current = false;
      }

      if (isSelectMenuInteraction) {
        return;
      }
      const inAccuracyPanel = !!target.closest('#rep-accuracy-dropdown');
      const inAccuracyToggle = !!target.closest('[data-rep-toggle="accuracy"]');
      const inSettingsToggle = !!target.closest('[data-rep-toggle="settings"]');
      const inTempoToggle = !!target.closest('#rep-tempo-container, #tempo-controls-container');

      if (!repTourOpen && !inAccuracyPanel && !inAccuracyToggle) {
        setShowAccuracyDetails(false);
      }
      if (
        settingsContainerRef.current &&
        !settingsContainerRef.current.contains(target) &&
        !settingsToggleRef.current?.contains(target) &&
        !shouldIgnoreSettingsClose &&
        !repTourOpen
      ) {
        setShowSettings(false);
      }
      // Close other dropdowns when opening one (tempo handles its own panel via TempoControls)
      if (inTempoToggle) {
        setShowAccuracyDetails(false);
        setShowSettings(false);
      }
      if (inAccuracyToggle) {
        setShowSettings(false);
      }
      if (inSettingsToggle) {
        setShowAccuracyDetails(false);
      }
    };

    // Don't listen on `click`: MUI's Select renders its portal+backdrop
    // between mousedown and mouseup on the trigger, and the browser then
    // computes the click event's target as the common ancestor of the
    // two — which is `document.body` when they're in different subtrees.
    // A body-targeted click matches no selector and is inside no ref, so
    // it falls through to the cross-close branches and silently dismisses
    // every open dropdown. pointerdown/touchstart/mousedown have stable
    // targets (the actual clicked element) and already cover the
    // click-outside-to-dismiss use case.
    const outsideInteractionEvents = ['pointerdown', 'touchstart', 'mousedown'] as const;
    outsideInteractionEvents.forEach((eventName) => {
      document.addEventListener(eventName, handleOutsideInteraction, true);
    });

    return () => {
      outsideInteractionEvents.forEach((eventName) => {
        document.removeEventListener(eventName, handleOutsideInteraction, true);
      });
    };
  }, [repTourOpen, setShowAccuracyDetails, setShowSettings]);

  let accuracyColor;
  if (accuracyPercent == null) {
    accuracyColor = accuracyColors[0];
  } else if (accuracyPercent >= 95) {
    accuracyColor = accuracyColors[1];
  } else if (accuracyPercent >= 75) {
    accuracyColor = accuracyColors[2];
  } else {
    accuracyColor = accuracyColors[3];
  }

  // Scale a pixel dimension by the measured header fit factor (1 = no scaling).
  const sp = (n: number) => `${n * headerScale}px`;

  const headerStyle = {
    // Slide up by full header height
    transform: headerVisible
      ? 'translateY(0)'
      : `translateY(-${headerWrapped ? wrappedHeaderHeightPx : 110}px)`,
    transition: 'transform 0.5s ease-in-out',
    position: 'fixed',
    width: '100%',
    zIndex: 10, // Ensure header is above other content
    // Other existing styles...
  };

  const tempoStylesOverride = {
    //height: mobileScreen ? '50px !important' : '50px',
    //right: 'unset !important',
    //left: mobileScreen ? '80px !important' : 'auto',
    //position: mobileScreen ? ('absolute' as const) : ('relative' as const),
    //top: '10px !important',
  };

  const accuracyStylesOverride = {
    backgroundColor: 'black',
    position: 'relative',
    transform:
      headerVisible || inPerformanceMode
        ? 'translateY(0)'
        : `translateY(${headerWrapped ? wrappedPillShiftPx : 110}px)`,
    transition: 'transform 0.5s ease-in-out',
  };

  return (
    <Box
      id="rep-header-container"
      ref={headerContainerRef}
      sx={{
        ...headerStyle,
        height: `${repertoireHeaderOneRowHeightPx(mobileScreen)}px`,
        display: 'flex',
        position: 'relative',
      }}
      // Repertoire.css sets the height on the id; two rows need their own.
      style={headerWrapped ? { height: 'auto', minHeight: '70px' } : undefined}
    >
      <Box
        ref={headerRowRef}
        sx={{
          background: '#040B13',
          width: '100%',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          ...(headerWrapped && { paddingTop: '8px', paddingBottom: '8px' }),
        }}
      >
        <Box
          ref={headerBackRef}
          sx={{
            position: 'relative',
            marginLeft: '30px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
            <Box
              sx={{
                ...backcontainerStyles,
                ...(repTourOpen && { position: 'relative', zIndex: -1 }),
              }}
              onClick={onBack}
            >
              <img style={{ height: '100%' }} src={RepBackArrow} />
            </Box>
          </Box>
        </Box>

        <Box
          ref={headerClusterRef}
          sx={{
            display: 'flex',
            alignItems: 'center',
            // Two rows: the controls flow onto a second row, right-aligned.
            ...(headerWrapped && {
              flex: '1 1 0',
              minWidth: 0,
              paddingRight: '8px',
              flexWrap: 'wrap',
              justifyContent: 'flex-end',
              columnGap: sp(16),
              rowGap: '8px',
            }),
          }}
        >
          <Box
            sx={{
              display: headerWrapped ? 'contents' : 'flex',
              height: '100%',
              alignItems: 'center',
              justifyContent: 'center',
              gap: sp(16),
            }}
          >
            <TempoControls
              playing={false}
              tempoToggleRef={tempoToggleRef}
              tempoContainerRef={tempoContainerRef}
              tempoInputRef={tempoInputRef}
              styleOverride={tempoStylesOverride}
              isRepertoire={true}
            />

            <Box id={REPERTOIRE_ACCURACY_CONTROL_ID}>
              <Box sx={{ position: 'relative' }}>
                {showAccuracyDetails && (
                  <AccuracyContainer
                    accuracyContainerRef={accuracyContainerRef}
                    repertoirePlaybackMode={repertoirePlaybackMode}
                    onRepertoirePlaybackModeChange={onRepertoirePlaybackModeChange}
                    anchorSx={accuracyAnchorSx}
                  />
                )}
              </Box>
              <Box
                ref={accuracyToggleRef}
                role="button"
                aria-label="Accuracy"
                aria-disabled={!graded}
                tabIndex={graded ? 0 : -1}
                sx={{
                  ...controlsStyles,
                  height: sp(54),
                  width: sp(54),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  ...(!graded && {
                    opacity: 0.4,
                    filter: 'grayscale(1)',
                    cursor: 'default',
                    '&:hover': { background: 'none', boxShadow: 'none', cursor: 'default' },
                  }),
                }}
                data-rep-toggle="accuracy"
                onClick={toggleAccuracyDetails}
                onKeyDown={(event) => {
                  if (event.key !== 'Enter' && event.key !== ' ') return;
                  event.preventDefault();
                  toggleAccuracyDetails();
                }}
              >
                <img
                  style={{ ...controlsImagesStyles, maxHeight: sp(46), maxWidth: sp(46) }}
                  src={AccuracyMusicIcon}
                />
              </Box>
            </Box>
            <LoopingToggleButton sp={sp} />
            <Box
              sx={{
                display: headerWrapped || headerCompact ? 'none' : undefined,
                height: sp(60),
                marginLeft: sp(16),
                marginRight: sp(16),
                borderRight: '2px solid #525969',
              }}
            />
            <Box
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: sp(24) }}
            >
              {/* <Box sx={{...controlsStyles2, boxSizing: 'content-box', border: inPerformanceMode ? '4px solid green' : 'none'}}> */}

              <Box
                sx={{
                  ...controlsStyles2,
                  // boxSizing: 'content-box',
                  // border: inPerformanceMode ? '4px solid green' : 'none'
                  background: inPerformanceMode ? 'rgba(160,255,118,.5)' : 'unset',
                  boxShadow: inPerformanceMode ? '0px 0px 5px 5px rgb(160,255,118,.5)' : 'unset',
                }}
                id="rep-performance-mode"
              >
                <img
                  style={{ maxHeight: sp(32) }}
                  src={PerformanceIcon}
                  onClick={() => togglePerformanceMode()}
                />
              </Box>
              <Box ref={settingsToggleRef} sx={{ position: 'relative', display: 'flex' }}>
                {showSettings && (
                  <SettingsContainer
                    setShowSettings={setShowSettings}
                    settingsContainerRef={settingsContainerRef}
                    transposeSemitones={transposeSemitones}
                    showTransposeControl={showTransposeControl}
                    onTransposeChange={onTransposeChange}
                    modeConversion={modeConversion}
                    onModeConversionChange={onModeConversionChange}
                    zoomLevel={zoomLevel}
                    setZoomLevel={setZoomLevel}
                    anchorSx={settingsAnchorSx}
                  />
                )}
                <Box
                  sx={controlsStyles2}
                  role="button"
                  aria-label="Settings"
                  tabIndex={0}
                  data-rep-toggle="settings"
                  onClick={() => setShowSettings(!showSettings)}
                  onKeyDown={(event) => {
                    if (event.key !== 'Enter' && event.key !== ' ') return;
                    event.preventDefault();
                    setShowSettings(!showSettings);
                  }}
                >
                  <img style={{ maxHeight: sp(32) }} src={SettingsIcon} />
                </Box>
              </Box>
            </Box>
            <Box
              sx={{
                display: headerWrapped || headerCompact ? 'none' : undefined,
                height: sp(60),
                marginLeft: sp(16),
                marginRight: sp(16),
                borderRight: '2px solid #525969',
              }}
            />
          </Box>

          <Box
            ref={headerAccuracyPillRef}
            sx={{
              display: 'flex',
              position: 'relative',
              border: `2px solid ${listenOnlyPlayback ? listenAccent.color : '#40E16D'}`,
              height: sp(50),
              borderRadius: sp(30),
              alignItems: 'center',
              marginRight: headerWrapped ? 0 : sp(20),
              // Mobile: solid black fill (clipped by the border radius) so the
              // pill reads clearly against the white score; desktop stays transparent.
              backgroundColor: listenOnlyPlayback
                ? listenAccent.surface
                : mobileScreen
                  ? '#000000'
                  : 'transparent',
              boxShadow: listenOnlyPlayback ? listenAccent.shadow : 'none',
              transform: accuracyStylesOverride.transform,
              transition: accuracyStylesOverride.transition,
            }}
          >
            {/* A compact header (short window) shows the value only. */}
            <RepertoireReadoutLabel
              text={listenOnlyPlayback ? 'LISTEN' : 'ACCURACY'}
              compact={headerCompact}
              sx={{ ...accuracyStyles, fontSize: sp(17), marginLeft: sp(18) }}
            />
            {/* showAccuracy ? */}
            <Box
              sx={{
                ...accuracyStyles,
                width: sp(mobileScreen ? 70 : 90),
                marginLeft: sp(8),
                marginBottom: '1px',
                marginRight: sp(18),
                fontSize: listenOnlyPlayback ? sp(15) : sp(22),
                color: listenOnlyPlayback ? '#F3C6B8' : accuracyColor,
                textAlign: 'center',
              }}
            >
              {listenOnlyPlayback
                ? 'NO SCORE'
                : accuracyPercent != null
                  ? `${accuracyPercent?.toFixed(1)}%`
                  : '--'}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

interface AccuracyContainerProps {
  accuracyContainerRef: React.RefObject<HTMLDivElement | null>;
  repertoirePlaybackMode: RepertoirePlaybackMode;
  onRepertoirePlaybackModeChange: (nextMode: RepertoirePlaybackMode) => void;
  anchorSx: Record<string, unknown>;
}

const RepertoirePlaybackModeSelector = ({
  repertoirePlaybackMode,
  onRepertoirePlaybackModeChange,
}: {
  repertoirePlaybackMode: RepertoirePlaybackMode;
  onRepertoirePlaybackModeChange: (nextMode: RepertoirePlaybackMode) => void;
}) => (
  <Box
    id="rep-playback-mode-selector"
    sx={{
      margin: '18px 16px 20px',
      padding: '12px 9px',
      borderRadius: '14px',
      border: '1px solid rgba(236, 242, 255, 0.12)',
      backgroundColor: 'rgba(8, 17, 31, 0.92)',
      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04)',
    }}
  >
    <Box
      sx={{
        marginBottom: '14px',
        textAlign: 'center',
        fontSize: '18px',
        lineHeight: '24px',
        letterSpacing: '1px',
        color: '#CED0D4',
        fontWeight: 'bold',
      }}
    >
      Playback
    </Box>
    <Box
      role="radiogroup"
      aria-label="Playback mode"
      sx={{
        display: 'grid',
        gridTemplateColumns: '1fr',
        gap: '8px',
      }}
    >
      {REPERTOIRE_PLAYBACK_MODE_OPTIONS.map((opt) => {
        const selected = repertoirePlaybackMode === opt.value;
        const accent = getRepertoirePlaybackModeAccent(opt.value);
        const PlaybackIcon = getRepertoirePlaybackModeIcon(opt.value);

        return (
          <Box
            key={opt.value}
            component="button"
            type="button"
            role="radio"
            aria-checked={selected}
            aria-label={opt.label}
            onClick={() => onRepertoirePlaybackModeChange(opt.value)}
            sx={{
              appearance: 'none',
              width: '100%',
              minHeight: '72px',
              border: `1px solid ${selected ? accent.border : 'rgba(236, 242, 255, 0.10)'}`,
              borderRadius: '10px',
              padding: '10px 9px',
              display: 'grid',
              gridTemplateColumns: '56px minmax(0, 1fr) 24px',
              alignItems: 'center',
              gap: '12px',
              color: '#ECECED',
              background: selected
                ? `linear-gradient(135deg, ${accent.surface}, rgba(36, 48, 86, 0.78))`
                : 'rgba(243, 247, 255, 0.045)',
              boxShadow: selected ? accent.shadow : 'none',
              fontFamily: 'Lato',
              cursor: 'pointer',
              textAlign: 'left',
              transition:
                'background 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease',
              '&:hover': {
                borderColor: accent.border,
                background: selected
                  ? `linear-gradient(135deg, ${accent.surface}, rgba(36, 48, 86, 0.86))`
                  : 'rgba(243, 247, 255, 0.075)',
                transform: 'translateY(-1px)',
              },
            }}
          >
            <Box
              sx={{
                width: '48px',
                height: '48px',
                borderRadius: '9px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: selected ? accent.color : '#AEB8CE',
                backgroundColor: selected ? accent.surface : 'rgba(236,242,255,0.07)',
              }}
            >
              <PlaybackIcon sx={{ fontSize: 32 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Box
                sx={{
                  fontSize: '15px',
                  lineHeight: 1.15,
                  fontWeight: 900,
                  color: '#F6F8FF',
                }}
              >
                {opt.label}
              </Box>
              <Box
                sx={{
                  marginTop: '4px',
                  fontSize: '12px',
                  lineHeight: 1.2,
                  color: '#AEB8CE',
                  whiteSpace: 'normal',
                }}
              >
                {opt.description}
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: accent.color,
              }}
            >
              {selected ? (
                <CheckRoundedIcon sx={{ fontSize: 20 }} />
              ) : (
                <Box
                  sx={{
                    width: '7px',
                    height: '7px',
                    borderRadius: '50%',
                    backgroundColor: 'rgba(174, 184, 206, 0.52)',
                  }}
                />
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  </Box>
);

const AccuracyContainer = (props: AccuracyContainerProps) => {
  const { accuracyContainerRef, repertoirePlaybackMode, onRepertoirePlaybackModeChange, anchorSx } =
    props;
  const repertoirePlaybackModesEnabled = useFeatureFlag(FeatureFlag.USE_REPERTOIRE_PLAYBACK_MODES);
  const listenOnlyPlayback = repertoirePlaybackMode === 'listen';

  const data = useSelector((state: MainAppReducer) => state.mainAppReducer);
  const { errorRecognitionActive } = data;
  const dispatch = useDispatch();

  return (
    <Box
      sx={{
        // Anchored 5px below the header bottom (see useHeaderPopupAnchor).
        ...anchorSx,
        zIndex: 10,
        minWidth: '350px',
        borderRadius: '8px',
        boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.25)',
        padding: '16px',
        backgroundColor: '#050B13',
        // Opacity-only entrance — this popup anchors the repertoire tour's
        // accuracy step, so a translateY slide would move it out from under the
        // already-measured tour tooltip (MUS-275). Fade without translating.
        animation: 'fadeIn 0.2s ease-out',
        '@keyframes fadeIn': {
          '0%': {
            opacity: 0,
          },
          '100%': {
            opacity: 1,
          },
        },
      }}
      ref={accuracyContainerRef}
      id="rep-accuracy-dropdown"
    >
      <Box
        sx={{
          position: 'absolute',
          top: '-10px', // Half of the carot size to move it above the modal
          left: '40px', // Align this with the hamburger icon
          width: '0',
          height: '0',
          borderLeft: '16px solid transparent',
          borderRight: '16px solid transparent',
          borderBottom: '16px solid #050B13', // Carot color should match modal background
        }}
      />
      <Box sx={detailsTitleTextStyles}>
        <Box
          sx={{
            textAlign: 'center',
            marginTop: '6px',
            marginBottom: '16px',
            fontSize: '20px',
            position: 'relative',
          }}
        >
          ACCURACY
        </Box>
        {repertoirePlaybackModesEnabled && (
          <RepertoirePlaybackModeSelector
            repertoirePlaybackMode={repertoirePlaybackMode}
            onRepertoirePlaybackModeChange={onRepertoirePlaybackModeChange}
          />
        )}
        <Box
          sx={{
            textAlign: 'center',
            marginTop: repertoirePlaybackModesEnabled ? '18px' : '32px',
            marginBottom: '8px',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              textAlign: 'center',
              display: 'flex',
              alignItems: 'center',
              gap: '7px',
              fontSize: '18px',
              color: '#CED0D4',
              fontWeight: 'bold',
            }}
          >
            Show Feedback
          </Box>
        </Box>
        <Box
          sx={{
            textAlign: 'center',
            marginTop: '30px',
            justifyContent: 'center',
            display: 'flex',
            gap: '12px',
          }}
        >
          <Box
            sx={{
              opacity: listenOnlyPlayback ? 0.42 : 1,
              filter: listenOnlyPlayback ? 'grayscale(0.8)' : 'none',
              '&:hover': { cursor: listenOnlyPlayback ? 'not-allowed' : 'pointer' },
            }}
            onClick={() => {
              if (listenOnlyPlayback) return;
              dispatch(
                appActions.setErrorRecognitionActive({
                  ...errorRecognitionActive,
                  left: !errorRecognitionActive.left,
                }),
              );
            }}
          >
            <img
              src={errorRecognitionActive.left ? HandActive : HandInactive}
              style={{
                maxHeight: '76px',
                transform: errorRecognitionActive.left ? 'none' : 'scaleX(-1)',
              }}
            />
          </Box>
          <Box
            sx={{
              opacity: listenOnlyPlayback ? 0.42 : 1,
              filter: listenOnlyPlayback ? 'grayscale(0.8)' : 'none',
              '&:hover': { cursor: listenOnlyPlayback ? 'not-allowed' : 'pointer' },
            }}
            onClick={() => {
              if (listenOnlyPlayback) return;
              dispatch(
                appActions.setErrorRecognitionActive({
                  ...errorRecognitionActive,
                  right: !errorRecognitionActive.right,
                }),
              );
            }}
          >
            <img
              src={errorRecognitionActive.right ? HandActive : HandInactive}
              style={{
                maxHeight: '76px',
                transform: errorRecognitionActive.right ? 'scaleX(-1)' : 'none',
              }}
            />
          </Box>
        </Box>
        <Box sx={{ display: 'flex', padding: '20px 14px', margin: '2px 0px' }}></Box>
      </Box>
    </Box>
  );
};

interface SettingsContainerProps {
  setShowSettings: (value: boolean) => void;
  settingsContainerRef: React.RefObject<HTMLDivElement | null>;
  transposeSemitones: number;
  showTransposeControl: boolean;
  onTransposeChange: (nextSemitones: number) => void;
  modeConversion: ParallelModeConversion;
  onModeConversionChange: (nextMode: ParallelModeConversion) => void;
  zoomLevel: number;
  setZoomLevel: (zoomLevel: number) => void;
  anchorSx: Record<string, unknown>;
}

const SettingsContainer = (props: SettingsContainerProps) => {
  const audioRecognitionEnabled = useFeatureFlag(FeatureFlag.USE_AUDIO_RECOGNITION);
  const pianoInputOffered = useEffectiveGrading();
  const { preferredInputMode, setPreferredInputMode, openInputSetupModal } = useMidiContext();
  const {
    addListener: addAudioRecognitionListener,
    removeListener: removeAudioRecognitionListener,
    calibrationProfile,
  } = useAudioRecognition();
  const {
    setShowSettings: _setShowSettings,
    settingsContainerRef,
    transposeSemitones,
    showTransposeControl,
    onTransposeChange,
    modeConversion,
    onModeConversionChange,
    zoomLevel,
    setZoomLevel,
    anchorSx,
  } = props;
  const dispatch = useDispatch();
  // The cog's distance from the screen's right edge, once measured.
  const cogRight = typeof anchorSx.right === 'string' ? anchorSx.right : undefined;

  const data = useSelector((state: MainAppReducer) => state.mainAppReducer);
  const rep = useSelector((state: RepertoireReducer) => state.repertoireReducer);
  const { userData } = data;
  const isMobile = useMediaQuery('(max-width: 960px)');
  const [audioDiagnosticsVisible, setAudioDiagnosticsVisible] = useState(() =>
    readStoredBoolean(REPERTOIRE_PLAY_AUDIO_DIAGNOSTICS_VISIBLE_STORAGE_KEY),
  );
  const [audioDiagnosticsSnapshot, setAudioDiagnosticsSnapshot] =
    useState<AudioDiagnosticsSnapshot | null>(null);
  const audioDiagnosticsFramesRef = useRef<AudioDiagnosticsFrame[]>([]);
  const audioDiagnosticsVisibleRef = useRef(audioDiagnosticsVisible);
  const latestAudioDiagnosticsSnapshotRef = useRef<AudioDiagnosticsSnapshot | null>(null);
  const lastAudioDiagnosticsUpdateMsRef = useRef(0);

  useEffect(() => {
    audioDiagnosticsVisibleRef.current = audioDiagnosticsVisible;
    writeStoredBoolean(
      REPERTOIRE_PLAY_AUDIO_DIAGNOSTICS_VISIBLE_STORAGE_KEY,
      audioDiagnosticsVisible,
    );
    if (audioDiagnosticsVisible) {
      setAudioDiagnosticsSnapshot(latestAudioDiagnosticsSnapshotRef.current);
    }
  }, [audioDiagnosticsVisible]);

  useEffect(() => {
    if (!audioRecognitionEnabled) return;

    const listener = {
      name: 'repertoire-play-audio-diagnostics-listener',
      onFrame: (frame: RecognitionFrame) => {
        if (!audioDiagnosticsVisibleRef.current) return;

        const capturedAtMs = Date.now();
        if (
          capturedAtMs - lastAudioDiagnosticsUpdateMsRef.current <
          AUDIO_DIAGNOSTICS_UI_INTERVAL_MS
        ) {
          return;
        }

        const diagnosticFrame = buildAudioDiagnosticsFrame(frame, [], [], capturedAtMs);
        const nextFrames = [...audioDiagnosticsFramesRef.current, diagnosticFrame].slice(
          -AUDIO_DIAGNOSTICS_MAX_FRAMES,
        );
        const snapshot = buildAudioDiagnosticsSnapshot(nextFrames);
        audioDiagnosticsFramesRef.current = nextFrames;
        latestAudioDiagnosticsSnapshotRef.current = snapshot;

        if (snapshot) {
          lastAudioDiagnosticsUpdateMsRef.current = capturedAtMs;
          setAudioDiagnosticsSnapshot(snapshot);
        }
      },
    };

    addAudioRecognitionListener(listener);

    return () => {
      removeAudioRecognitionListener(listener);
    };
  }, [addAudioRecognitionListener, audioRecognitionEnabled, removeAudioRecognitionListener]);

  const openAudioCalibrationSetup = useCallback(() => {
    setPreferredInputMode('audio');
    openInputSetupModal();
  }, [openInputSetupModal, setPreferredInputMode]);

  const handleShowAudioDiagnostics = useCallback(() => {
    audioDiagnosticsFramesRef.current = [];
    latestAudioDiagnosticsSnapshotRef.current = null;
    lastAudioDiagnosticsUpdateMsRef.current = 0;
    audioDiagnosticsVisibleRef.current = true;
    setAudioDiagnosticsSnapshot(null);
    setAudioDiagnosticsVisible(true);
  }, []);

  const inputStatus = useMemo(() => {
    if (!audioRecognitionEnabled) {
      return {
        label: 'Audio unavailable',
        detail: 'Use MIDI input',
        tone: 'idle' as const,
      };
    }

    if (preferredInputMode !== 'audio') {
      return {
        label: 'Acoustic input available',
        detail: 'Switch to microphone input',
        tone: 'idle' as const,
      };
    }

    if (!calibrationProfile) {
      return {
        label: 'Audio needs calibration',
        detail: 'Calibration required',
        tone: 'warning' as const,
      };
    }

    return {
      label: 'Listening to piano',
      detail: 'Audio recognition ready',
      tone: 'ready' as const,
    };
  }, [audioRecognitionEnabled, calibrationProfile, preferredInputMode]);

  const settingsRowSx = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    minHeight: '38px',
    textAlign: 'left',
    position: 'relative',
    ...repSettingsSideSx,
    marginBottom: '12px',
    color: '#EAF2FF',
    fontSize: '15px',
  } as const;
  const settingsFieldSx = {
    textAlign: 'left',
    position: 'relative',
    ...repSettingsSideSx,
    marginBottom: '16px',
    minWidth: '230px',
  } as const;

  return (
    <Box
      id="rep-settings-container"
      sx={{
        // Anchored 5px below the header bottom, right-aligned to the settings
        // toggle (see useHeaderPopupAnchor).
        ...anchorSx,
        // Kept on the screen on a phone held upright (see repSettingsPanelPlacement).
        ...repSettingsPanelPlacement(cogRight),
        zIndex: 20,
        maxHeight: 'calc(100vh - 96px)',
        borderRadius: '8px',
        boxShadow:
          '0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12)',
        padding: '16px',
        backgroundColor: '#050B13',
        // `visible` so the caret (positioned above the box) isn't clipped — the
        // inner content box below handles its own scrolling.
        overflow: 'visible',
        // Opacity-only entrance (see the accuracy popup above) — keeps this
        // box stationary so the tour tooltip that may anchor here doesn't end
        // up offset after the Popper's one-shot measurement (MUS-275).
        animation: 'fadeIn 0.2s ease-out',
        '@keyframes fadeIn': {
          '0%': {
            opacity: 0,
          },
          '100%': {
            opacity: 1,
          },
        },
      }}
      ref={settingsContainerRef}
    >
      <Box
        sx={{
          position: 'absolute',
          // Matches the other popups' 10px gap below the header (the popup top
          // is 10px below the header bottom; this -10px lifts the caret into
          // that gap).
          top: '-10px',
          // The popup's right edge is aligned to the settings icon's right edge,
          // so a 32px-wide caret with right:0 centres its tip on the ~32px gear.
          // Where the panel slid right to stay on the screen, the caret moves in
          // by as much.
          right: repSettingsCaretRight(cogRight),
          width: '0',
          height: '0',
          borderLeft: '16px solid transparent',
          borderRight: '16px solid transparent',
          borderBottom: '16px solid #050B13',
        }}
      />

      <Box
        sx={{
          ...detailsTitleTextStyles,
          maxHeight: 'calc(100vh - 140px)',
          overflowY: 'auto',
          overflowX: 'hidden',
          paddingRight: '6px',
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(121, 216, 255, 0.45) rgba(255,255,255,0.06)',
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'rgba(255,255,255,0.06)',
            borderRadius: '999px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(121, 216, 255, 0.45)',
            borderRadius: '999px',
          },
        }}
      >
        <Box
          sx={{
            textAlign: 'center',
            marginTop: '6px',
            marginBottom: '16px',
            fontSize: '20px',
            position: 'relative',
          }}
        >
          SETTINGS
        </Box>

        <Box sx={{ ...settingsRowSx, marginTop: '18px' }}>
          <CustomSwitch
            checked={userData?.audio_on ?? true}
            onChange={() => {
              dispatch(appActions.setAudioOn({ audioOn: !(userData?.audio_on ?? true) }));
            }}
          />
          <Box component="span">Keyboard Sound</Box>
        </Box>

        <Box sx={settingsRowSx} id="rep-metronome-cursor-container">
          <CustomSwitch
            checked={userData?.rep_metronome_sound ?? true}
            onChange={() => {
              dispatch(appActions.setRepMetronomeSound(!(userData?.rep_metronome_sound ?? true)));
            }}
          />
          <Box component="span">Metronome Sound</Box>
        </Box>

        <Box sx={settingsRowSx}>
          <CustomSwitch
            checked={userData?.rep_downbeats_sound ?? true}
            onChange={() => {
              dispatch(appActions.setRepDownbeatsSound(!(userData?.rep_downbeats_sound ?? true)));
            }}
          />
          <Box component="span">Accent Downbeats</Box>
        </Box>

        {volumeControlsAvailable() && (
          <Box sx={{ ...repSettingsSideSx, marginBottom: '12px' }}>
            <VolumeControls title="Volume" />
          </Box>
        )}

        <Box sx={repSettingsSideSx}>
          <PlayInputMenuEntry />
        </Box>

        <Box sx={settingsRowSx}>
          <CustomSwitch
            checked={rep.cursorVisible}
            onChange={() => {
              const newValue = !rep.cursorVisible;
              dispatch(repertoireActions.setCursorVisible(newValue));
              // Persist to dedicated localStorage key — separate from redux-persist
              // to prevent lifecycle dispatches from corrupting the saved value.
              try {
                localStorage.setItem('museflow_cursor_visible', String(newValue));
              } catch {
                /* noop */
              }
            }}
          />
          <Box component="span">Cursor</Box>
        </Box>
        {isMobile && (
          <Box sx={{ ...settingsFieldSx, marginTop: '8px' }}>
            <Box sx={{ marginBottom: '8px' }}>Zoom Level</Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <Remove
                sx={{ color: '#ECECED', cursor: 'pointer', fontSize: '20px' }}
                onClick={() => setZoomLevel(Math.max(0.5, zoomLevel - 0.05))}
              />
              <input
                className="custom-slider"
                type="range"
                value={Math.max(0.5, Math.min(1, zoomLevel))}
                onChange={(e) => setZoomLevel(e.target.valueAsNumber)}
                min={0.5}
                max={1}
                step={0.05}
                style={{ flex: 1 }}
              />
              <Add
                sx={{ color: '#ECECED', cursor: 'pointer', fontSize: '20px' }}
                onClick={() => setZoomLevel(Math.min(1, zoomLevel + 0.05))}
              />
            </Box>
          </Box>
        )}
        {showTransposeControl && (
          <Box sx={settingsFieldSx}>
            <Box sx={{ marginBottom: '8px' }}>Transpose</Box>
            <TextField
              select
              fullWidth
              size="small"
              value={`${transposeSemitones}`}
              onChange={(event) => {
                onTransposeChange(Number(event.target.value));
              }}
              SelectProps={{ MenuProps: repertoireSettingsSelectMenuProps }}
              sx={repertoireSettingsSelectSx}
            >
              {TRANSPOSE_SEMITONE_OPTIONS.map((step) => (
                <MenuItem key={step} value={`${step}`}>
                  {getTransposeLabel(step)}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        )}

        {showTransposeControl && (
          <Box sx={settingsFieldSx}>
            <Box sx={{ marginBottom: '8px' }}>Scale / Mode</Box>
            <TextField
              select
              fullWidth
              size="small"
              value={modeConversion}
              onChange={(event) => {
                onModeConversionChange(event.target.value as ParallelModeConversion);
              }}
              SelectProps={{ MenuProps: repertoireSettingsSelectMenuProps }}
              sx={repertoireSettingsSelectSx}
            >
              {MODE_CONVERSION_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        )}

        {/* No Piano Input setup while play is ungraded. */}
        {audioRecognitionEnabled && pianoInputOffered && (
          <>
            <Box sx={repertoireAudioInputStatusCardSx(inputStatus.tone)}>
              <Box sx={repertoireAudioInputDotSx(inputStatus.tone)} />
              <Box sx={{ minWidth: 0 }}>
                <Box sx={repertoireAudioInputTitleSx}>{inputStatus.label}</Box>
                <Box sx={repertoireAudioInputDetailSx}>{inputStatus.detail}</Box>
              </Box>
              <Button
                variant="outlined"
                size="small"
                startIcon={<ReplayIcon />}
                onClick={openAudioCalibrationSetup}
                sx={repertoireAudioInputActionButtonSx}
              >
                {calibrationProfile ? 'Recalibrate' : 'Calibrate'}
              </Button>
            </Box>

            <Box sx={{ ...repSettingsSideSx, marginBottom: '14px' }}>
              <Button
                variant="outlined"
                onClick={() => {
                  if (audioDiagnosticsVisible) {
                    setAudioDiagnosticsVisible(false);
                  } else {
                    handleShowAudioDiagnostics();
                  }
                }}
                sx={repertoireAudioDiagnosticsButtonSx}
              >
                {audioDiagnosticsVisible ? 'Hide audio diagnostics' : 'Show audio diagnostics'}
              </Button>
            </Box>

            {audioDiagnosticsVisible && (
              <Box
                sx={{
                  ...repSettingsSideSx,
                  marginBottom: '16px',
                  overflowX: 'auto',
                  borderRadius: '12px',
                  border: '1px solid rgba(243,247,255,0.12)',
                }}
              >
                <AudioDiagnosticsPanel
                  snapshot={audioDiagnosticsSnapshot}
                  frames={audioDiagnosticsFramesRef.current}
                  darkMode
                />
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

const controlsStyles = {
  // Draw the ring as an inner border so the icon's total footprint (border
  // included) stays at the set size and matches the tempo icon, rather than
  // the 4px ring growing the circle outward beyond it.
  boxSizing: 'border-box',
  height: '54px',
  border: '4px solid #525969',
  borderRadius: '50%',
  '&:hover': {
    background: 'rgba(222,144,118,.5)',
    cursor: 'pointer',
    transition: '0.3s',
    borderRadius: '50%',
    boxShadow: '0px 0px 5px 5px rgb(222,144,118,.5)',
  },
};
const controlsStyles2 = {
  // height: '54px',
  // border: '4px solid #525969',
  borderRadius: '50%',
  '&:hover': {
    background: 'rgba(222,144,118,.5)',
    cursor: 'pointer',
    transition: '0.3s',
    borderRadius: '50%',
    boxShadow: '0px 0px 5px 5px rgb(222,144,118,.5)',
  },
};
const controlsImagesStyles = {
  marginTop: '-1px',
  maxHeight: '54px',
};

const accuracyStyles = {
  color: '#ECECEC',
  fontFamily: 'Lato',
  fontSize: '17px',
  fontWeight: '700',
  lineHeight: '20px',
  letterSpacing: '1px',
  textAlign: 'left',
};

const backcontainerStyles = {
  marginRight: '18px',
  height: '25px',
  '&:hover': {
    background: 'rgba(222,144,118,.5)',
    cursor: 'pointer',
    transition: '0.3s',
    borderRadius: '50%',
    boxShadow: '0px 0px 5px 5px rgb(222,144,118,.5)',
  },
};

type FooterContainerProps = {
  headerVisible: boolean;
  keepTransportVisible: boolean;
  scoreTheme: ScoreTheme;
  playLevel: () => void;
  softRefreshRepertoire: () => Promise<void>;
  afterPrimaryAction?: () => void;
  onShowInfo: () => void; // NEW: open repertoire tour
  mobileScreen: boolean;
};

const FooterContainer = (props: FooterContainerProps) => {
  const {
    mobileScreen,
    scoreTheme,
    headerVisible,
    keepTransportVisible,
    playLevel,
    softRefreshRepertoire,
    afterPrimaryAction,
    onShowInfo,
  } = props;
  const _data = useSelector((state: MainAppReducer) => state.mainAppReducer);
  const repertoireReducerData = useSelector((state: RepertoireReducer) => state.repertoireReducer);
  const { repertoireEnded } = repertoireReducerData;
  // On a phone while playing only a small Pause is left; the rest of the
  // footer slides away and its empty band lets taps through to the score.
  const { footerOnScreen, transportOnly, showRestart, compactPause } = footerTransportLayout({
    headerVisible,
    keepTransportVisible,
    playing: repertoireReducerData.repertoirePlaying,
    ended: repertoireEnded,
  });
  const transportOffsets = compactPause ? TRANSPORT_OFFSETS.compact : TRANSPORT_OFFSETS.full;
  const { isLoopingActive } = useRepertoirePlayContext();

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: '0px',
        left: '0px',
        width: '100vw',
        display: 'flex',
        height: '100px',
        minHeight: '100px',
        justifyContent: 'space-between',
        backgroundColor: 'rgba(255, 0, 255, 0)',
        transform: footerOnScreen ? 'translateY(0)' : 'translateY(140px)', // Slide up by full header height
        transition: 'transform 0.5s ease-in-out',
        pointerEvents: transportOnly ? 'none' : 'auto',
      }}
    >
      <Box
        sx={{
          zIndex: 1,
          position: 'absolute',
          top: 34,
          left: 30,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          transform: transportOnly ? 'translateY(140px)' : 'none',
          transition: 'transform 0.5s ease-in-out',
        }}
      >
        <IconButton
          id="repertoire-info-button"
          aria-label="Repertoire info"
          onClick={() => onShowInfo()}
          sx={{
            color: '#ECECEC',
            background: 'rgba(36,48,86,0.9)',
            border: '1px solid #525969',
            '&:hover': { background: 'rgba(36,48,86,1.0)' },
            width: 36,
            height: 36,
          }}
        >
          <InfoOutlinedIcon fontSize="small" />
        </IconButton>
        <Fullscreen show_label={false} use_dark_icon={true} />
      </Box>
      <Box
        sx={{
          position: 'absolute',
          right: transportOffsets.right,
          bottom: transportOffsets.bottom,
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: '20px',
          pointerEvents: 'auto',
        }}
      >
        {showRestart && (
          <Box
            onClick={() => {
              if (isLoopingActive) {
                NotifyPhaserEvents.RestartLoopFromBeginning();
              } else {
                softRefreshRepertoire();
              }
            }}
          >
            <Box
              sx={{
                ...restartButtonStyles(mobileScreen),
                ...(scoreTheme === 'dimPaper' ? dimPaperRestartButtonChrome : {}),
              }}
            >
              {scoreTheme === 'dimPaper' ? (
                <ReplayIcon sx={dimPaperRestartIconChrome} />
              ) : (
                <img style={{ maxWidth: '100%', maxHeight: '100%' }} src={Restart} />
              )}
            </Box>
          </Box>
        )}
        <Box
          onClick={() => {
            playLevel();
            afterPrimaryAction?.();
          }}
        >
          <Box
            sx={
              compactPause
                ? compactPauseButtonStyles
                : repertoireReducerData.repertoirePlaying
                  ? pauseButtonStyles(mobileScreen)
                  : playButtonStyles(mobileScreen)
            }
            id="rep-play-button"
          >
            {compactPause ? (
              <Box component="span" aria-hidden="true" sx={compactPauseIconStyles}>
                <Box component="span" sx={compactPauseBarStyles} />
                <Box component="span" sx={compactPauseBarStyles} />
              </Box>
            ) : repertoireReducerData.repertoirePlaying ? (
              <Box component="span" aria-hidden="true" sx={pauseIconStyles}>
                <Box component="span" sx={pauseBarStyles} />
                <Box component="span" sx={pauseBarStyles} />
              </Box>
            ) : (
              <>
                {repertoireEnded ? (
                  <img
                    style={{ maxWidth: '100%', maxHeight: '100%', margin: '0px' }}
                    src={RestartBig}
                  />
                ) : (
                  <img
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      margin: '0px',
                      borderRadius: '100%',
                    }}
                    src={Play}
                  />
                )}
              </>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const playButtonStyles = (mobileScreen: boolean) => ({
  ...transportPlayButtonChrome,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: mobileScreen ? '70px' : '105px',
  height: mobileScreen ? '70px' : '105px',
  lineHeight: 0,
  overflow: 'visible',
});

const pauseButtonStyles = (mobileScreen: boolean) => ({
  ...playButtonStyles(mobileScreen),
  backgroundColor: '#CA7C62',
  boxShadow: '0px 8px 8px rgba(0, 0, 0, 0.28)',
});

const pauseIconStyles = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  height: '54px',
};

const pauseBarStyles = {
  display: 'block',
  width: '15px',
  flex: '0 0 15px',
  height: '54px',
  borderRadius: '8px',
  backgroundColor: '#E7C4B9',
};

// The pause left on a phone while playing: a 48px target, translucent so
// the score shows through, in the corner below the staff.
const compactPauseButtonStyles = {
  ...transportPlayButtonChrome,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: `${COMPACT_PAUSE_SIZE_PX}px`,
  height: `${COMPACT_PAUSE_SIZE_PX}px`,
  lineHeight: 0,
  backgroundColor: 'rgba(202, 124, 98, 0.75)',
  boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.2)',
};

const compactPauseIconStyles = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '5px',
  height: '20px',
};

const compactPauseBarStyles = {
  display: 'block',
  width: '6px',
  flex: '0 0 6px',
  height: '20px',
  borderRadius: '3px',
  backgroundColor: '#F3E1DA',
};

const restartButtonStyles = (mobileScreen: boolean) => ({
  ...transportRestartButtonChrome,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: mobileScreen ? '50px' : '80px',
  height: mobileScreen ? '50px' : '80px',
  flex: mobileScreen ? '0 0 50px' : '0 0 80px',
});

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- used in commented JSX below
const TopRevealZone: React.FC<{ onReveal: () => void }> = ({ onReveal }) => (
  <Box
    onClick={onReveal}
    sx={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      height: 'max(28px, env(safe-area-inset-top))',
      zIndex: 3,
      background: 'transparent',
    }}
  />
);

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- used in commented JSX above
const FloatingHeaderToggle: React.FC<{ onToggle: () => void }> = ({ onToggle }) => (
  <Box
    onClick={onToggle}
    sx={{
      position: 'fixed',
      right: 12,
      top: 'calc(env(safe-area-inset-top) + 12px)',
      zIndex: 3,
      background: 'rgba(36,48,86,0.9)',
      borderRadius: '20px',
      padding: '8px 10px',
      border: '1px solid #525969',
      '&:active': { transform: 'scale(0.98)' },
    }}
  >
    <KeyboardArrowDownIcon style={{ color: '#ECECEC' }} />
  </Box>
);

export default RepertoirePlay;

const detailsTitleTextStyles = {
  color: '#FFFFFF',
  fontFamily: 'Lato',
  fontSize: '15px',
  fontHeight: '700',
  lineHeight: '24px',
  letterSpacing: '1px',
  textAlign: 'left',
};
